import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Form,
  Input,
  Select,
  Upload,
  InputNumber,
  Switch,
  Button,
  Modal,
  message,
  Spin,
  Row,
  Col,
  Space,
} from "antd";
import { CameraOutlined, LoadingOutlined } from "@ant-design/icons";
import type { UploadChangeParam, UploadFile } from "antd/es/upload";
import {
  fetchListing,
  updateListing,
  listingPublish,
  listingPause,
  listingResume,
  listingSold,
  listingRelist,
  deleteListing,
  uploadMedia,
  addListingPhoto,
  fetchCities,
  type Listing,
  type CreateListingInput,
  type City,
} from "../api/auth";
import { ErrorNote, PageHeader, PageShell, Panel, StatusTag } from "../components/Page";

const { TextArea } = Input;
const { Option } = Select;
const { Dragger } = Upload;

/** The subset of listing fields the edit form round-trips. */
interface EditFormValues {
  title: string;
  description: string;
  condition: CreateListingInput["condition"];
  listingType: CreateListingInput["listingType"];
  brandId: string;
  modelId?: string;
  customModelName?: string;
  price: number;
  negotiable: boolean;
  modelYear?: number;
  manufacturingYear?: number;
  cityId: string;
  areaText?: string;
  contactPreference: CreateListingInput["contactPreference"];
  allowVideoCall: boolean;
}

export default function EditBikePage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const listingId = id;
  const [form] = Form.useForm<EditFormValues>();

  const [listing, setListing] = useState<Listing | null>(null);
  const [cities, setCities] = useState<City[]>([]);
  const [cityLoading, setCityLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [photoFiles, setPhotoFiles] = useState<File[]>([]);

  useEffect(() => {
    (async () => {
      setCityLoading(true);
      try {
        setCities(await fetchCities());
      } catch {
        // ignore
      } finally {
        setCityLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      if (!listingId) return;
      setError(null);
      try {
        const data = await fetchListing(listingId);
        setListing(data);
        // The API returns price as a money object `{ amount, currency }`; the
        // form wants a plain number.
        const price = typeof data.price === "number" ? data.price : (data.price as { amount?: number })?.amount ?? 0;
        form.setFieldsValue({
          title: data.title,
          description: data.description,
          condition: data.condition,
          listingType: data.listingType,
          brandId: data.brandId,
          modelId: data.modelId ?? "",
          customModelName: data.customModelName ?? "",
          price,
          negotiable: data.negotiable,
          modelYear: data.modelYear ?? 0,
          manufacturingYear: data.manufacturingYear ?? 0,
          cityId: data.location?.cityId ?? data.cityId ?? "",
          areaText: data.location?.areaText ?? data.areaText ?? "",
          contactPreference: data.contactPreference,
          allowVideoCall: !!data.allowVideoCall,
        });
      } catch (err: unknown) {
        setError((err as { message?: string })?.message ?? "Could not load the bike.");
      }
    })();
  }, [listingId, form]);

  const onFinish = async (values: EditFormValues) => {
    if (!listingId) return;
    setSaving(true);
    try {
      await updateListing(listingId, values);
      message.success("Changes saved.");
    } catch (err: unknown) {
      message.error((err as { message?: string })?.message ?? "Could not save changes.");
    } finally {
      setSaving(false);
    }
  };

  const runAction = async (fn: () => Promise<unknown>, label: string) => {
    if (!listingId) return;
    try {
      await fn();
      message.success(`Bike ${label}.`);
      if (listingId) {
        const data = await fetchListing(listingId);
        setListing(data);
      }
    } catch (err: unknown) {
      message.error((err as { message?: string })?.message ?? `Could not ${label}.`);
    }
  };

  const confirmDelete = () => {
    if (!listingId) return;
    Modal.confirm({
      title: "Delete this bike?",
      content: "The listing and its photos will be removed. This can't be undone.",
      okText: "Delete",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          await deleteListing(listingId);
          message.success("Bike deleted.");
          navigate("/vehicles/all");
        } catch (err: unknown) {
          message.error((err as { message?: string })?.message ?? "Could not delete.");
        }
      },
    });
  };

  const onChangePhoto = (info: UploadChangeParam) => {
    const fileList = info.fileList as UploadFile[];
    const files = fileList
      .filter((f) => f.originFileObj instanceof File)
      .map((f) => f.originFileObj as File);
    setPhotoFiles(files);
  };

  const addPhotos = async () => {
    if (!listingId || photoFiles.length === 0) return;
    try {
      for (let i = 0; i < photoFiles.length; i++) {
        const asset = await uploadMedia(photoFiles[i]);
        await addListingPhoto(listingId, { assetId: asset.id, position: i, isCover: false });
      }
      message.success(`${photoFiles.length} photo${photoFiles.length !== 1 ? "s" : ""} added.`);
      setPhotoFiles([]);
      const data = await fetchListing(listingId);
      setListing(data);
    } catch (err: unknown) {
      message.error((err as { message?: string })?.message ?? "Could not add photos.");
    }
  };

  if (error)
    return (
      <PageShell>
        <ErrorNote error={error} />
      </PageShell>
    );
  if (!listingId)
    return (
      <PageShell>
        <ErrorNote error={new Error("Invalid bike id.")} />
      </PageShell>
    );
  if (!listing)
    return (
      <PageShell>
        <div className="flex justify-center py-16">
          <Spin />
        </div>
      </PageShell>
    );

  const status = listing.status;

  // One lifecycle action, chosen by the state the bike is actually in. "Publish"
  // on a paused or sold bike is a button that can only fail — `resume` and
  // `relist` are the transitions the API actually accepts.
  const lifecycle =
    status === "PUBLISHED"
      ? { label: "Pause", danger: true, run: () => listingPause(listingId), done: "paused" }
      : status === "PAUSED"
        ? { label: "Resume", danger: false, run: () => listingResume(listingId), done: "resumed" }
        : status === "SOLD" || status === "EXPIRED"
          ? { label: "Relist", danger: false, run: () => listingRelist(listingId), done: "relisted" }
          : status === "REMOVED"
            ? null
            : { label: "Publish", danger: false, run: () => listingPublish(listingId), done: "published" };

  return (
    <PageShell>
      <PageHeader
        eyebrow="Inventory"
        title={listing.title}
        description={
          <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <StatusTag status={status} />
            <span className="font-mono text-[12px] text-ink-faint">{listing.publicId}</span>
          </span>
        }
        actions={
          <Space size="small">
            {lifecycle && (
              <Button
                type={lifecycle.danger ? "default" : "primary"}
                danger={lifecycle.danger}
                onClick={() => runAction(lifecycle.run, lifecycle.done)}
                disabled={saving}
              >
                {lifecycle.label}
              </Button>
            )}
            {status !== "SOLD" && status !== "REMOVED" && (
              <Button
                danger
                onClick={() => runAction(() => listingSold(listingId), "marked sold")}
                disabled={saving}
              >
                Mark sold
              </Button>
            )}
            <Button danger onClick={confirmDelete} disabled={saving}>
              Delete
            </Button>
          </Space>
        }
      />

      <Panel title="Bike details">
        <Form form={form} layout="vertical" onFinish={onFinish} colon={false} className="bh-form">
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} lg={8}>
              <Form.Item name="title" label="Title" rules={[{ required: true }]}>
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} lg={4}>
              <Form.Item name="price" label="Price (BDT)" rules={[{ type: "number", min: 0 }]}>
                <InputNumber />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} lg={4}>
              <Form.Item name="condition" label="Condition">
                <Select>
                  <Option value="NEW">New</Option>
                  <Option value="USED">Used</Option>
                  <Option value="REFURBISHED">Refurbished</Option>
                  <Option value="DAMAGED_FOR_PARTS">Damaged (for parts)</Option>
                  <Option value="RECONDITIONED_IMPORTED">Reconditioned import</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} lg={4}>
              <Form.Item name="negotiable" label="Negotiable" valuePropName="checked">
                <Switch />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} lg={4}>
              <Form.Item name="modelYear" label="Model year">
                <InputNumber min={1900} max={2100} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} lg={4}>
              <Form.Item name="manufacturingYear" label="Year of manufacture">
                <InputNumber min={1900} max={2100} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} lg={4}>
              <Form.Item name="contactPreference" label="Contact preference">
                <Select>
                  <Option value="CHAT_ONLY">Chat only</Option>
                  <Option value="CHAT_AND_CALL">Chat and call</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} lg={4}>
              <Form.Item name="allowVideoCall" label="Allow video call" valuePropName="checked">
                <Switch />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} lg={8}>
              <Form.Item name="cityId" label="City" rules={[{ required: true, message: "City is required" }]}>
                <Select
                  placeholder="Select City"
                  loading={cityLoading}
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    (option?.children as unknown as string ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                >
                  {cities.map((c) => (
                    <Option key={c.id} value={c.id}>
                      {c.name} {c.nameBn ? `(${c.nameBn})` : ""}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} lg={8}>
              <Form.Item name="areaText" label="Area / locality">
                <Input />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="description" label="Description" rules={[{ required: true }]}>
            <TextArea rows={4} />
          </Form.Item>
          <div className="mt-4">
            <Button type="primary" htmlType="submit" disabled={saving} icon={<LoadingOutlined spin={saving} />}>
              Save changes
            </Button>
          </div>
        </Form>

        {/* ── Add more photos ──────────────────────────────────────────── */}
        <div className="mt-6 border-t border-line pt-5">
          <h3 className="mb-3 text-[13px] font-semibold text-ink">Add more photos</h3>
          <p className="mb-3 text-sm text-ink-muted">
            {listing.photoCount} photo{listing.photoCount === 1 ? "" : "s"} on this bike. Publishing
            needs at least 3.
          </p>
          <Dragger
            onChange={onChangePhoto}
            beforeUpload={() => false}
            multiple
            fileList={photoFiles.map((f) => ({
              originFileObj: f,
              uid: `${f.name}-${f.size}`,
              name: f.name,
            })) as UploadFile[]}
            accept="image/*"
          >
            <p className="ant-upload-text">
              <CameraOutlined className="bh-upload-icon" />
              Click or drag photos here
            </p>
          </Dragger>
          {photoFiles.length > 0 && (
            <div className="mt-3">
              <Button type="primary" onClick={addPhotos} disabled={!listingId}>
                Attach {photoFiles.length} photo{photoFiles.length !== 1 ? "s" : ""}
              </Button>
            </div>
          )}
        </div>
      </Panel>
    </PageShell>
  );
}